import { clear, el } from './dom';

/**
 * The coachmark: a card of power-user shortcuts that appears when you pick up a tool and then gets
 * out of the way.
 *
 * ## Why it is temporary, and why it leaves early
 *
 * `Ctrl`+wheel to size the brush is the single most useful thing to know about the sculpt tool and
 * the single least discoverable — it is a modifier on a gesture that already means something else.
 * The tool panel has it written down, but it is written down in the same small grey block as six
 * other lines, at the far edge of the screen from the ground being sculpted; people do not read it
 * because nothing ever asks them to.
 *
 * So it is said once, near the middle of the screen, at the moment it becomes true — and then it
 * goes. It goes on a timer, and it goes *immediately* the first time the user uses any of the
 * commands on it, because at that point the card is telling someone something they have just
 * demonstrated they know. Same reason it does not come back later in the session once dismissed
 * that way (see `Hud`): a hint that keeps reappearing after it has been learned is an advert.
 *
 * ## It never takes the pointer
 *
 * `pointer-events: none` throughout. It floats over the canvas, which is where the user is about to
 * press the mouse — a coachmark that could swallow the first stroke of a drag would cost more than
 * it teaches. Dismissal is by time or by use, never by having to aim at a close button.
 */

/** One line of the card: the keys, and what they do. */
export interface ShortcutHint {
  /** Key caps, rendered as `<kbd>`. */
  readonly keys: readonly string[];
  /**
   * How the caps relate. `chord` means held together, `either` means either one will do — a
   * distinction worth drawing, because `Ctrl + wheel` and `, .` look identical without it and mean
   * opposite things.
   */
  readonly join?: 'chord' | 'either';
  /** What holding or pressing them does, in the app's voice. */
  readonly text: string;
}

/**
 * The sculpt tool's advanced commands.
 *
 * Ordered by how much time they save, not by how the keyboard is laid out: size is adjusted
 * constantly, strength often, the two held modifiers are the corrections you make while looking at
 * a result, and cycling the mode is the one thing the panel already makes obvious.
 */
export const SCULPT_SHORTCUTS: readonly ShortcutHint[] = [
  { keys: ['Ctrl', 'wheel'], text: 'Brush size' },
  { keys: ['Shift', 'wheel'], text: 'Strength' },
  { keys: ['Alt'], text: 'Hold to invert — lower where you raise' },
  { keys: ['Shift'], text: 'Hold to smooth as you go' },
  { keys: [',', '.'], join: 'either', text: 'Previous / next mode' },
];

/**
 * The planting tool's advanced commands.
 *
 * `Ctrl` leads, and it is listed twice on purpose. Hold and tap are genuinely two commands on one
 * key (see `PlacementTool`), and folding them into a single line — "Ctrl switches mode" — would
 * describe neither: the reader would try one of them, find it works, and never learn the other.
 * Everything below them is brush trim, in the order it is reached for.
 */
export const PLANT_SHORTCUTS: readonly ShortcutHint[] = [
  { keys: ['Ctrl'], text: 'Hold to borrow the other mode — one plant, or a drift' },
  { keys: ['Ctrl'], text: 'Tap to keep it' },
  { keys: ['Ctrl', 'wheel'], text: 'Brush size' },
  { keys: ['Shift', 'wheel'], text: 'How tightly the drift packs' },
  { keys: ['[', ']'], join: 'either', text: 'Previous / next plant' },
];

/** How long the card holds before fading, in milliseconds. */
export const HINT_HOLD_MS = 7000;

/** Length of the fade, matching the transition in `hud.css`. */
const FADE_MS = 420;

export class ToolHint {
  readonly root: HTMLElement;

  private readonly title: HTMLElement;
  private readonly list: HTMLElement;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;
  private showing = false;

  constructor() {
    this.title = el('span', { class: 'lg-coach__title' });
    this.list = el('dl', { class: 'lg-coach__list' });
    this.root = el(
      'div',
      {
        class: 'lg-coach',
        // Polite, not assertive: it is an aside about the tool, not something that has gone wrong.
        attrs: { role: 'note', 'aria-live': 'polite', hidden: true },
      },
      this.title,
      this.list,
    );
  }

  get isShowing(): boolean {
    return this.showing;
  }

  /** Put the card up. Re-showing what is already up only restarts the clock. */
  show(title: string, hints: readonly ShortcutHint[], holdMs = HINT_HOLD_MS): void {
    this.clearTimers();
    this.title.textContent = title;
    clear(this.list);
    for (const hint of hints) {
      this.list.appendChild(
        el(
          'div',
          { class: 'lg-coach__row' },
          el('dt', { class: 'lg-coach__keys', html: keyCaps(hint.keys, hint.join ?? 'chord') }),
          el('dd', { class: 'lg-coach__text', text: hint.text }),
        ),
      );
    }

    this.showing = true;
    this.root.hidden = false;
    // Next frame, so the element is laid out unhidden before the class that transitions it lands —
    // set in the same tick, the browser has nothing to animate from and the card simply appears.
    onNextFrame(() => {
      if (this.showing) this.root.classList.add('is-visible');
    });
    this.holdTimer = setTimeout(() => this.dismiss(), Math.max(0, holdMs));
  }

  /** Fade it out. Safe to call when nothing is up. */
  dismiss(): void {
    this.clearTimers();
    if (!this.showing) return;
    this.showing = false;
    this.root.classList.remove('is-visible');
    // Hidden only once the fade has finished, so the card is not yanked out mid-transition — and so
    // it leaves the accessibility tree rather than lingering as an invisible note.
    this.fadeTimer = setTimeout(() => {
      this.root.hidden = true;
      this.fadeTimer = null;
    }, FADE_MS);
  }

  dispose(): void {
    this.clearTimers();
    this.showing = false;
    this.root.remove();
  }

  private clearTimers(): void {
    if (this.holdTimer !== null) clearTimeout(this.holdTimer);
    if (this.fadeTimer !== null) clearTimeout(this.fadeTimer);
    this.holdTimer = null;
    this.fadeTimer = null;
  }
}

/** Keys as `<kbd>` caps, joined by what they mean. Escaped, because they are drawn as markup. */
export function keyCaps(keys: readonly string[], join: 'chord' | 'either' = 'chord'): string {
  const separator = join === 'either' ? 'or' : '+';
  return keys
    .map((key) => `<kbd>${escapeHtml(key)}</kbd>`)
    .join(`<span class="lg-coach__join">${separator}</span>`);
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"]/g, (ch) =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;',
  );
}

function onNextFrame(callback: () => void): void {
  if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(() => callback());
  else setTimeout(callback, 16);
}
